import * as React from 'react';
import type { EventContentArg } from '@fullcalendar/core';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { CalendarBlank as CalendarIcon } from '@phosphor-icons/react/dist/ssr/CalendarBlank';
import { Clock as ClockIcon } from '@phosphor-icons/react/dist/ssr/Clock';

import { dayjs } from '@/lib/v2/dayjs';

export interface EventContentProps extends EventContentArg {
  event: EventContentArg['event'] & { 
    extendedProps: { 
      description?: string; 
      priority?: 'high' | 'medium' | 'low';
      type?: string;
      color?: string;
    } 
  };
}

export function EventContent(arg: EventContentProps): React.JSX.Element {
  const { priority = 'low', type, color: eventColor } = arg.event.extendedProps;

  // Use event type color if available, otherwise fall back to priority colors
  const color = eventColor || (
    priority === 'high'
      ? 'var(--mui-palette-error-main)'
      : priority === 'medium'
        ? 'var(--mui-palette-warning-main)'
        : 'transparent'
  );

  const startTime = arg.event.start ? dayjs(arg.event.start).format('h:mm A') : null;
  const endTime = arg.event.end ? dayjs(arg.event.end).format('h:mm A') : null;

  const isShortEvent =
    arg.event.start && arg.event.end && dayjs(arg.event.end).diff(dayjs(arg.event.start), 'minute') < 45;

  const isAllDay = arg.event.allDay;

  return (
    <Box
      sx={{
        position: 'relative',
        height: '100%',
        minHeight: isAllDay ? '32px' : '28px',
        borderRadius: '6px',
        background: '#ffffff',
        border: '1px solid #e5e7eb',
        borderLeft: `4px solid ${color}`,
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        overflow: 'hidden',
        '&:hover': {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
          borderColor: '#d1d5db',
          transform: 'translateY(-1px)',
          transition: 'all 0.2s ease-in-out',
        },
      }}
    >
      {/* Content */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: isShortEvent ? 'row' : 'column',
          alignItems: isShortEvent ? 'center' : 'flex-start',
          gap: isShortEvent ? 1 : 0.5,
          p: isAllDay ? '6px 12px' : '4px 12px',
          height: '100%',
          position: 'relative',
        }}
      >
        {/* Time section */}
        {!isAllDay && startTime ? <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              flexShrink: 0,
            }}
          >
            <ClockIcon color={color} size={12} style={{ opacity: 0.8 }} />
            <Typography
              sx={{
                color: '#6b7280',
                fontWeight: 600,
                fontSize: '11px',
                lineHeight: 1,
                whiteSpace: 'nowrap',
              }}
              variant="caption"
            >
              {isShortEvent ? `${startTime} - ${endTime}` : startTime}
            </Typography>
          </Box> : null}

        {/* All day indicator */}
        {isAllDay ? <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              flexShrink: 0,
            }}
          >
            <CalendarIcon color={color} size={12} style={{ opacity: 0.8 }} />
            <Typography
              sx={{
                color: '#6b7280',
                fontWeight: 600,
                fontSize: '11px',
                lineHeight: 1,
              }}
              variant="caption"
            >
              All Day
            </Typography>
          </Box> : null}

        {/* Title section */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.25,
          }}
        >
          <Typography
            sx={{
              color: '#111827',
              fontWeight: 600,
              fontSize: isAllDay ? '13px' : '12px',
              lineHeight: 1.2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: isShortEvent ? 1 : 2,
              WebkitBoxOrient: 'vertical',
            }}
            variant="body2"
          >
            {arg.event.title}
          </Typography>

          {/* Type chip for longer events */}
          {type && !isShortEvent ? <Chip
              label={type}
              size="small"
              sx={{
                height: '16px',
                fontSize: '10px',
                fontWeight: 600,
                backgroundColor: alpha(color, 0.1),
                color,
                border: `1px solid ${alpha(color, 0.2)}`,
                '& .MuiChip-label': {
                  px: 0.75,
                },
                alignSelf: 'flex-start',
              }}
            /> : null}
        </Box>

        {/* Priority indicator */}
        {priority === 'high' && (
          <Box
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: color,
              boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
            }}
          />
        )}
      </Box>
    </Box>
  );
}
